import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Checkbox, Typography } from "@mui/material"
import { checkboxClasses } from "@mui/material/Checkbox"
import { useMemo, useState } from "react"
import { extraColors } from "../../utils/theme"
import {
  Cell,
  HeaderCell,
  HelperText,
  LegendContainer,
  LegendHeader,
  LegendRow,
  LegendTable,
  SearchBox,
} from "./LegendStyles"
import { SHAPE_ICONS, type BaseLegendItemData } from "./legendUtils"

export interface ColumnDefinition<T extends BaseLegendItemData> {
  header: string | React.ReactNode
  render: (item: T) => React.ReactNode
  width?: string
}

interface TimeLegendProps<T extends BaseLegendItemData> {
  items: T[]
  hiddenIds: Set<string>
  onToggle: (id: string) => void
  onToggleAll: () => void
  columns?: ColumnDefinition<T>[]
  searchable?: boolean
  searchPlaceholder?: string
  helperText?: string
  headerTitle?: string
  /** Use full drawer/page width when legend sits below the chart. */
  fullWidth?: boolean
  maxVisibleSeries?: number
  atVisibleCap?: boolean
  capHelperText?: string
}

/**
 * Generic time series legend component
 * Handles visibility toggles, optional search, and custom columns
 */
export const TimeLegend = <T extends BaseLegendItemData>({
  items,
  hiddenIds,
  onToggle,
  onToggleAll,
  columns = [],
  searchable = false,
  searchPlaceholder = "Search",
  helperText = "Select a row to isolate it in the chart",
  headerTitle,
  fullWidth = false,
  maxVisibleSeries,
  atVisibleCap = false,
  capHelperText,
}: TimeLegendProps<T>) => {
  const [searchTerm, setSearchTerm] = useState("")

  // Calculate grid columns: checkbox (50px) + name (1fr) + additional columns
  const gridColumns = 2 + columns.length

  const filteredItems = useMemo(() => {
    if (!searchTerm || !searchable) return items
    return items.filter(item =>
      item.label.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [items, searchTerm, searchable])

  const maxSelectable = maxVisibleSeries
    ? Math.min(maxVisibleSeries, filteredItems.length)
    : filteredItems.length
  const visibleInFilter = filteredItems.filter(
    item => !hiddenIds.has(item.id)
  ).length
  const allSelected = maxSelectable > 0 && visibleInFilter >= maxSelectable
  const indeterminate = visibleInFilter > 0 && visibleInFilter < maxSelectable

  return (
    <LegendContainer
      sx={
        fullWidth ? { width: "100%", marginLeft: 0, marginTop: 0 } : undefined
      }
    >
      {searchable && (
        <SearchBox
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      )}
      <LegendTable>
        {(headerTitle || columns.length > 0) && (
          <LegendHeader columns={gridColumns}>
            <HeaderCell>
              <Checkbox
                checked={allSelected}
                indeterminate={indeterminate}
                onChange={onToggleAll}
                size="small"
                sx={{
                  color: extraColors.status.navyLight,
                  [`&.${checkboxClasses.checked}`]: {
                    color: extraColors.status.navyLight,
                  },
                  [`&.${checkboxClasses.indeterminate}`]: {
                    color: extraColors.status.navyLight,
                  },
                }}
              />
            </HeaderCell>
            <Cell>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: 12 }}
              >
                {headerTitle}
              </Typography>
            </Cell>
            {columns.map((col, idx) => (
              <Cell key={idx}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontSize: 12 }}
                >
                  {col.header}
                </Typography>
              </Cell>
            ))}
          </LegendHeader>
        )}
        {filteredItems.map(item => {
          const selected = !hiddenIds.has(item.id)
          const cannotEnable = atVisibleCap && !selected
          return (
            <LegendRow
              key={item.id}
              active={selected}
              columns={gridColumns}
              onClick={() => {
                if (!cannotEnable) onToggle(item.id)
              }}
              sx={
                cannotEnable
                  ? { opacity: 0.45, cursor: "not-allowed" }
                  : undefined
              }
            >
              <HeaderCell>
                <Checkbox
                  checked={selected}
                  disabled={cannotEnable}
                  size="small"
                  sx={{
                    color: extraColors.status.navyLight,
                    [`&.${checkboxClasses.checked}`]: {
                      color: extraColors.status.navyLight,
                    },
                  }}
                />
              </HeaderCell>
              <Cell>
                <FontAwesomeIcon
                  icon={SHAPE_ICONS[item.shape]}
                  color={item.color}
                  style={{ fontSize: 14 }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    ml: 1,
                  }}
                >
                  {item.label}
                </Typography>
              </Cell>
              {columns.map((col, idx) => (
                <Cell key={idx}>{col.render(item)}</Cell>
              ))}
            </LegendRow>
          )
        })}
      </LegendTable>
      {atVisibleCap && capHelperText ? (
        <HelperText>{capHelperText}</HelperText>
      ) : (
        helperText && <HelperText>{helperText}</HelperText>
      )}
    </LegendContainer>
  )
}
